<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase" v-if="event">
          {{ event.name }}
        </h1>
      </div>
    </div>

    <loader v-if="!loaded || submittingOrder" />
    <panel v-else-if="!event || !ticket.is_opened" class="pt-6">
      <strong class="mb-2 uppercase">{{ event.name }} - Booking Page</strong>
      <p>Ticket Booking is not open at this time.</p>
    </panel>

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not have access to use this system.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col py-6 px-4">
      <h3 class="flex items-center leading-none px-4">{{ event.name }}</h3>
      <div class="px-4 mt-4" v-if="unpaidOrders && unpaidOrders.length >0" >
        Unpaid Orders:
        <ul>
          <li v-for="(unpaidOrder, i) in unpaidOrders" :key="i" class="p-2">
            Order created by {{unpaidOrder.email}} ({{unpaidOrder.created_at | date}}) - {{unpaidOrder.event_users.length}} ticket(s): {{unpaidOrder.sub_total | gbp}} <span v-if="unpaidOrder.vat"> + VAT </span>.
            <button type="button" @click="paidForOrder(unpaidOrder.id)" class="bg-primary text-white py-1 px-1 mx-2 rounded text-sm">Pay Here</button>
            <button type="button" @click="deleteUnpaidOrder(unpaidOrder.id)" class="bg-secondary text-white py-1 px-1 mx-2 rounded text-sm">Delete Order</button>
            <ul>
              <li v-for="(user, j) in unpaidOrder.event_users" :key="j">
                {{ user.email }}
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div class="flex flex-wrap mt-8">
        <div class="mb-6 px-4 w-full lg:w-2/3">
          <div class="border border-grey-light">

            <!-- STAGE TABS -->
            <div class="w-full flex text-sm mb-6">
              <div @click="stage = i" class="flex flex-col w-1/3" :class="{'border-l border-r': i < 4}" v-for="(stageName, i) in stages" :key="i"
                   v-if="i!=2 || ticket.dietary_flag || ticket.allergy_flag">
                <div class="w-full h-full px-4 pt-3 pb-5 border-t-4" :class="[(i === stage ? 'border-t-4 border-primary' : 'border-transparent bg-grey-lighter'), (i === stage ? 'cursor-default' : 'cursor-pointer')]">
                  <div class="flex h-full items-center"><div class="w-8"><span class="w-6 h-6 rounded-full flex flex-col items-center justify-center text-white" :class="i === stage ? 'bg-primary' : 'bg-grey-dark'">{{ i + 1 }}</span></div> <div>{{ stageName }}</div></div>
                </div>
              </div>
            </div>

            <div class="bg-yellow-lighter flex p-4 mx-4 mb-4 rounded text-sm leading-normal" v-if="!isEmpty(errorBag.errors)">
              <i class="fas fa-exclamation-triangle mr-3 fa-2x pt-2"></i>
              <div class="flex-grow">
                <strong class="block mb-3 text-sm">There were problems with your order. Please resolve these issues and try again.</strong>
                <ul class="pl-6">
                  <li v-for="(error, i) in errorBag.errors" :key="i">{{ error.join(' ') }}</li>
                </ul>
              </div>
              <i class="fas fa-times text-xl ml-3 text-grey-darker hover:text-primary cursor-pointer" @click="errorBag.clear()"></i>
            </div>

            <div class="w-full text-sm mb-2 p-4">
              <!-- STAGE FORM -->
              <component :is="stageView" :event="event" :ticket="ticket" v-model="order" :order="order" :errorBag="errorBag" />

              <div class="w-full text-right" v-if="stage < 3">
                <button type="button" class="bg-primary py-3 px-20 border border-primary bg-primary hover:bg-black text-white hover:text-white focus:outline-none" @click="stage++">
                  {{ stage == 1 && order.additional_users.length === 0 ? 'Skip Step &gt;&gt;' : 'Next Step &gt;&gt;' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <TicketBookingOrderSummary :ticket="ticket" :stage="stage" :registrants="registrants" :subtotal="subtotal" :vat="vat" :total="total" @submit="submitOrder" />

      </div>
    </div>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { isEmpty } from 'lodash'
import ErrorBag from '@/ErrorBag'

import TicketBookingStage0 from './TicketBookingStage0'
import TicketBookingStage1 from './TicketBookingStage1'
import TicketBookingStage2 from './TicketBookingStage2'
import TicketBookingStage3 from './TicketBookingStage3'

import { mapState } from 'vuex'
import TicketBookingOrderSummary from './TicketBookingOrderSummary'

export default {
  name: 'TicketEventBooking',
  components: {
    TicketBookingStage0,
    TicketBookingStage1,
    TicketBookingStage2,
    TicketBookingStage3,
    TicketBookingOrderSummary
  },
  data () {
    return {
      loaded: false,
      officeAccess: true,
      event: null,
      ticket: null,
      unpaidOrders: null,
      order: {
        event_ticket_id: null,
        first_name: null,
        last_name: null,
        company: null,
        dietary_requirements: 'None',
        allergies: null,
        email: null,
        exclude_current_user: false,
        additional_users: []
      },
      stage: 0,
      stages: [
        'Your Details',
        'Additional Attendees',
        'Dietary Requirements',
        'Review & Payment'
      ],
      conference: null,
      errorBag: new ErrorBag(),
      submittingOrder: false,
      orderResponse: null
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile']),
    stageView () {
      return `ticket-booking-stage${this.stage}`
    },
    registrants () {
      let result = []
      if (!this.order.exclude_current_user && this.order.first_name) {
        let firstTicket = {
          first_name: this.order.first_name,
          last_name: this.order.last_name,
          company: this.order.company,
          email: this.order.email,
          dietary_requirements: this.order.dietary_requirements
        }
        result.push(firstTicket)
      }
      for (let additionalUser of this.order.additional_users) {
        result.push(additionalUser)
      }
      return result
    },
    subtotal () {
      let discountNum = 0
      let discountPertentage = 0
      if (this.ticket.is_free) {
        return 0
      }
      for (let rule of this.ticket.selected_discount_rules) {
        if (this.registrants.length >= rule.base_number) {
          if (rule.free_number) {
            discountNum = discountNum > rule.free_number ? discountNum : rule.free_number
          }
          if (rule.discount_percentage) {
            discountPertentage = discountPertentage > rule.discount_percentage ? discountPertentage : rule.discount_percentage
          }
        }
      }

      return this.ticket.price_after_discount && this.registrants.length
        ? (this.ticket.price_after_discount * (this.registrants.length - discountNum) * (1 - discountPertentage)) : 0
    },
    vat () {
      if (this.selectedOffice.country !== 'GB') {
        return 0
      }
      return this.subtotal * 0.2
    },
    total () {
      return this.subtotal + this.vat
    }
  },
  watch: {
    /* order: {
      handler () {
        this.setRegistrants()
        this.calculateTotals()
      },
      deep: true
    } */
  },
  async created () {
    await this.getEventTicket()
    if (this.ticket) {
      this.loaded = true
    }
    // await this.loadTicketOptions()
  },
  methods: {
    isEmpty,
    getEventTicket () {
      v2Api.get(`/event-tickets/${this.$route.params.eventTicketId}/office/${this.selectedOffice.id}`).then(response => {
        this.event = response.data.event
        this.ticket = response.data.ticket
        this.unpaidOrders = response.data.unpaid_orders
        this.order.event_ticket_id = this.ticket.ticket_id
        this.loaded = true
      })
    },
    submitOrder (paymentType) {
      this.errorBag.clear()
      this.order.office_id = this.selectedOffice.id
      this.order.payment_type_id = paymentType
      if (this.order.payment_type_id == null) {
        alert('Please select a payment type.')
        return
      }
      this.submittingOrder = true
      v2Api.post('/event-orders', this.order).then((response) => {
        this.orderResponse = response.data
        if (response.data.result) {
          window.location = response.data.checkout_url
        } else {
          alert('Sorry, there is something wrong. Please contact administrator')
          this.submittingOrder = false
        }
      }, (errors) => {
        this.submittingOrder = false
        this.errorBag.store(errors.response.data)
      })
    },
    paidForOrder (unpaidOrderId) {
      this.submittingOrder = true
      v2Api.get(`/event-orders/${unpaidOrderId}/start-payment`).then((response) => {
        this.orderResponse = response.data
        if (response.data.paid) {
          alert('This order is already paid')
        } else if (response.data.checkout_url) {
          window.location = response.data.checkout_url
        } else {
          alert('Sorry, there is something wrong. Please contact administrator')
        }
        this.submittingOrder = false
      }, (errors) => {
        this.submittingOrder = false
      })
    },
    deleteUnpaidOrder (unpaidOrderId) {
      if (confirm('Are you sure you want to delete this unpaid order?')) {
        this.submittingOrder = true
        v2Api.delete(`/event-orders/${unpaidOrderId}`).then((response) => {
          this.orderResponse = response.data
          if (!response.data.open) {
            alert('This order can not be removed because it is completed or cancelled.')
          } else if (response.data.result) {
            alert('Order deleted.')
            if (this.unpaidOrders.length <= 1) {
              this.unpaidOrders = null
            } else {
              let index = this.unpaidOrders.indexOf(
                find(this.unpaidOrders, (n) => n.id === unpaidOrderId)
              )

              if (index >= 0) {
                this.unpaidOrders.splice(index, 1)
              }
            }
          } else {
            alert('Sorry, there is something wrong. Please contact administrator')
          }
          this.submittingOrder = false
        }, (errors) => {
          this.submittingOrder = false
        })
      }
    }
  }
}
</script>

<style lang="scss">
input[type="radio"] {
  display: inline-block;
}
</style>
